export enum BdcPhoneTechType {
  VOICE = 1,
  FAX = 3,
  MOBILE = 5,
}

export const bdcPhoneTechTypeOptions = [
  { id: BdcPhoneTechType.VOICE, label: $localize`Voice` },
  { id: BdcPhoneTechType.FAX, label: $localize`Fax` },
  { id: BdcPhoneTechType.MOBILE, label: $localize`Mobile` },
]

export enum BdcContactProfileType {
  /**
   * Primary point of contact for the property.
   */
  GENERAL = 'general',

  /**
   * Contact for reservations.
   */
  RESERVATIONS = 'reservations',

  /**
   * Contact for accounts payable.
   */
  INVOICES = 'invoices',

  /**
   * Contact for questions about availability.
   */
  AVAILABILITY = 'availability',

  /**
   * Contact for photos, descriptions, and other website content.
   */
  SITE_CONTENT = 'site_content',

  /**
   * Contact for pricing and rate matters.
   */
  PARITY = 'parity',

  /**
   * Contact for special requests.
   */
  REQUESTS = 'requests',

  /**
   * Contact for central reservations. Applies to properties that manage reservations from another location.
   */
  CENTRAL_RESERVATIONS = 'central_reservations',
}

export type BdcContactProfile = {
  /**
   * Specifies the type of contact.
   * For a list of approved contact types, see ContactProfileType.
   */
  type: BdcContactProfileType
}

export type BdcContactAddress = {
  city_name?: string // Species the name of the city, town, or village.
  // state_prov_code?: string // Contains the State/province details.
  country_code: string // Species the two-letter country code.
  postal_code?: string // Specifies the postal/zip code.
  address_line?: string // Species the full street name and number.
  language_code?: string // Species the Booking.com Language code for the address details.
}

export type BdcContactPerson = {
  language_code: string // Species the contact's spoken language(s).
  gender?: string // Specifies the contact's gender.
  name: string // Specifies the contact person's full name.
  job_title?: string // Specifies the contact person's job title code.
}

export type BdcContactPhone = {
  phone_number: string // Specifies the international phone number.
  phone_tech_type: BdcPhoneTechType // Specifies the type of phone line/device.
  extension?: string // The extension number that must be dialled in addition to the phone_number.
}

export type BdcContact = {
  /**
   * Contains the contact type.
   */
  contact_profiles: BdcContactProfile[]

  /**
   * Contains the address details of the contact including country, city and postal code details.
   * You can set only one address per contact type.
   */
  address?: BdcContactAddress

  /**
   * Contains the contact person's name, gender, spoken language and job title.
   */
  contact_person: BdcContactPerson

  /**
   * Contains the phone number information.
   */
  phones: BdcContactPhone[]

  /**
   * Contains the email address details.
   * Make sure to specify a valid email address.
   */
  email: string
}

export interface BdcContactInfo {
  /**
   * Contains contact information.
   * You must create one contact details each for at least two contact types namely: General and Invoices.
   * Note that you can create at a maximum only one contact detail per property for the General and Invoices contact types.
   */
  contacts: BdcContact[]
}

export type BdcUpdatePropertiesContactsPayload = BdcContactInfo

export const bdcContactJobTitles = [
  'Reservations Employee',
  'Owner',
  'Reservations Manager',
  'E-Commerce Manager',
  'Finance Manager',
  'Revenue Manager',
  'Sales Executive',
  'Sales & Marketing Manager',
  'Front Office Manager',
  'Director of Business Development',
  'Marketing Manager',
  'Administration Employee',
  'Front Office Employee',
  'Rooms Division Manager',
  'General Manager',
  'Sales Manager',
  'Unknown',
]
